import { useState, useEffect, useRef } from "react";
import { Instagram, ExternalLink, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TikTok Icon Component
const TikTokIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Video Post Component - Simple Inline Playback (Mobile-Friendly)
const VideoPost = ({ post }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Lazy loading with IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px", threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Pause video when scrolling out of view
  useEffect(() => {
    if (!isVisible) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && videoRef.current && isPlaying) {
          videoRef.current.pause();
        }
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible, isPlaying]);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleVideoClick = (e) => {
    e.stopPropagation();
    // Toggle play/pause on tap
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  };

  const videoSrc = post.is_self_hosted ? `${BACKEND_URL}${post.video_url}` : post.video_url;

  return (
    <div
      ref={containerRef}
      className="instagram-item relative aspect-square overflow-hidden bg-[#1A1A1A]"
      data-testid={`instagram-video-${post.id}`}
    >
      {isVisible ? (
        <>
          {/* Direct Video Display - Inline Only */}
          <video
            ref={videoRef}
            src={videoSrc}
            className="w-full h-full object-cover"
            playsInline
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            preload="metadata"
            muted={false}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handlePause}
            onClick={handleVideoClick}
            style={{ 
              WebkitAppearance: 'none',
              objectFit: 'cover'
            }}
          />

          {/* Play/Pause Overlay - Shows when paused */}
          {!isPlaying && (
            <div 
              className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none"
            >
              <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                <Play className="w-7 h-7 text-[#0A0A0A] fill-current ml-1" />
              </div>
            </div>
          )}

          {/* Video Badge */}
          <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 rounded text-white text-xs font-medium flex items-center gap-1 pointer-events-none">
            <Play className="w-3 h-3 fill-current" />
            Video
          </div>

          {/* Caption overlay (visible when paused) */}
          {!isPlaying && post.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pointer-events-none">
              <p className="text-white text-sm font-medium line-clamp-2">
                {post.caption}
              </p>
            </div>
          )}
        </>
      ) : (
        // Placeholder while not visible
        <div className="w-full h-full bg-[#1A1A1A] animate-pulse flex items-center justify-center">
          <Play className="w-10 h-10 text-[#71717A]" />
        </div>
      )}
    </div>
  );
};

// Image Post Component with Lazy Loading
const ImagePost = ({ post }) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px", threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const imageSrc = post.image_url?.startsWith('/api') 
    ? `${BACKEND_URL}${post.image_url}` 
    : post.image_url;

  return (
    <a
      ref={containerRef}
      href={post.instagram_link || "#"}
      target={post.instagram_link ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="instagram-item group relative aspect-square overflow-hidden bg-[#1A1A1A]"
      data-testid={`instagram-image-${post.id}`}
      onClick={(e) => !post.instagram_link && e.preventDefault()}
    >
      {isVisible ? (
        <>
          {/* Loading placeholder */}
          {!isLoaded && (
            <div className="absolute inset-0 bg-[#1A1A1A] animate-pulse" />
          )}
          
          <img
            src={imageSrc}
            alt={post.caption || "Instagram Post"}
            className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
          />
          
          {/* Hover overlay with caption */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
            {post.caption && (
              <p className="text-white text-sm font-medium line-clamp-2">
                {post.caption}
              </p>
            )}
          </div>
          
          {/* Instagram icon overlay */}
          <div className="absolute inset-0 bg-[#c1ff72]/0 group-hover:bg-[#c1ff72]/20 transition-colors duration-300 flex items-center justify-center">
            <Instagram className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
          </div>
        </>
      ) : (
        <div className="w-full h-full bg-[#1A1A1A] animate-pulse" />
      )}
    </a>
  );
};

// Fallback posts if API fails
const fallbackPosts = [
  {
    id: "1",
    post_type: "image",
    image_url: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400&h=400&fit=crop",
    caption: "Techniktraining mit Fokus auf Ballkontrolle",
    instagram_link: "https://instagram.com/fussballtraining_aktas",
  },
  {
    id: "2",
    post_type: "image",
    image_url: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&h=400&fit=crop",
    caption: "Einzeltraining - Individuelles Coaching",
    instagram_link: "https://instagram.com/fussballtraining_aktas",
  },
  {
    id: "3",
    post_type: "image",
    image_url: "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=400&h=400&fit=crop",
    caption: "Sonntagstraining mit der Gruppe",
    instagram_link: "https://instagram.com/fussballtraining_aktas",
  },
  {
    id: "4",
    post_type: "image",
    image_url: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=400&h=400&fit=crop",
    caption: "Nachwuchsförderung ab 6 Jahren",
    instagram_link: "https://instagram.com/fussballtraining_aktas",
  },
  {
    id: "5",
    post_type: "image",
    image_url: "https://images.unsplash.com/photo-1600250395178-40fe752e5189?w=400&h=400&fit=crop",
    caption: "Spezielles Torwarttraining",
    instagram_link: "https://instagram.com/fussballtraining_aktas",
  },
  {
    id: "6",
    post_type: "image",
    image_url: "https://images.unsplash.com/photo-1570189046794-606e8291541d?w=400&h=400&fit=crop",
    caption: "Abendtraining unter Flutlicht",
    instagram_link: "https://instagram.com/fussballtraining_aktas",
  },
];

export const InstagramSection = () => {
  const [posts, setPosts] = useState(fallbackPosts);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/instagram/posts`);
        if (response.data && response.data.length > 0) {
          setPosts(response.data);
        }
      } catch (error) {
        // Using fallback Instagram posts
      } finally {
        setIsLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <section
      className="py-20 lg:py-32 bg-[#121212]"
      data-testid="instagram-section"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <Instagram className="w-6 h-6 text-[#c1ff72]" />
              <p className="text-xs uppercase tracking-[0.2em] text-[#c1ff72] font-semibold">
                @fussballtraining_aktas
              </p>
            </div>
            <h2 className="font-['Bebas_Neue'] text-4xl md:text-6xl lg:text-7xl uppercase tracking-tighter text-white">
              TRAININGS<span className="text-[#c1ff72]">EINBLICKE</span>
            </h2>
            <p className="text-[#A1A1AA] mt-4 max-w-xl mx-auto text-lg">
              Schau dir meine Trainingsvideos und Fotos an
            </p>
          </div>

          {/* Instagram Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-10" data-testid="instagram-grid">
            {posts.map((post) => (
              post.post_type === "video" && post.is_self_hosted && post.video_url ? (
                <VideoPost 
                  key={post.id} 
                  post={post} 
                />
              ) : (
                <ImagePost key={post.id} post={post} />
              )
            ))}
          </div>

          {/* Social Media Links */}
          <div className="bg-[#1A1A1A] border border-white/10 p-8 mb-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                {/* Instagram */}
                <a
                  href="https://instagram.com/fussballtraining_aktas"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] rounded-full flex items-center justify-center">
                    <Instagram className="w-8 h-8 text-white" />
                  </div>
                  <div className="hidden sm:block">
                    <p className="font-['Bebas_Neue'] text-xl text-white">@fussballtraining_aktas</p>
                    <p className="text-[#A1A1AA] text-sm">Instagram</p>
                  </div>
                </a>
                
                {/* TikTok */}
                <a
                  href="https://tiktok.com/@fussballtraining_aktas"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group"
                >
                  <div className="w-16 h-16 bg-[#000000] border border-white/20 rounded-full flex items-center justify-center">
                    <TikTokIcon size={32} className="text-white" />
                  </div>
                  <div className="hidden sm:block">
                    <p className="font-['Bebas_Neue'] text-xl text-white">@fussballtraining_aktas</p>
                    <p className="text-[#A1A1AA] text-sm">TikTok</p>
                  </div>
                </a>
              </div>
              
              <div className="flex gap-3">
                <Button
                  asChild
                  className="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] hover:opacity-90 text-white font-semibold px-6 py-5 rounded-none transition-all duration-300"
                >
                  <a
                    href="https://instagram.com/fussballtraining_aktas"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="instagram-profile-button"
                  >
                    <Instagram className="w-4 h-4 mr-2" />
                    FOLGEN
                  </a>
                </Button>
                <Button
                  asChild
                  className="bg-[#000000] hover:bg-[#1A1A1A] border border-white/20 text-white font-semibold px-6 py-5 rounded-none transition-all duration-300"
                >
                  <a
                    href="https://tiktok.com/@fussballtraining_aktas"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tiktok-profile-button"
                  >
                    <TikTokIcon size={16} className="mr-2" />
                    TIKTOK
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-[#71717A] mb-4">
              Folge mir für regelmäßige Trainingstipps und Einblicke
            </p>
            <Button
              asChild
              className="bg-transparent hover:bg-[#c1ff72] border-2 border-[#c1ff72] text-white hover:text-[#0A0A0A] font-semibold px-8 py-6 rounded-none transition-all duration-300 group"
            >
              <a
                href="https://instagram.com/fussballtraining_aktas"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-follow-button"
              >
                <Instagram className="w-5 h-5 mr-2" />
                JETZT AUF INSTAGRAM FOLGEN
                <ExternalLink className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </Button>
          </div>
        </div>
      </section>
  );
};

export default InstagramSection;
